import * as React from 'react'
import { inject, observer } from 'mobx-react'
import { Radio, Skeleton } from 'antd'
import { UncontrolledButtonDropdown, DropdownToggle, DropdownMenu, DropdownItem } from 'reactstrap'
import { parseAndDownloadCSV, GrPrice, orderedExportData } from '../../../../../utilities/helpers'
import { ReportFilter } from '../ReportFilter'
import { ReportFilterStore } from '../ReportFilterStore'
import { API } from '@getgreenline/homi-shared'
import Helmet from 'react-helmet'
import {
  TimePeriod,
  ISourcesReportData,
  SourceSalesReportFields,
  ISalesSourceQuery,
} from '@getgreenline/homi-shared/dist/models/Reports/ReportsV2'
import { Track } from '../../../../../Track'
import moment from 'moment'

interface Props {
  companyId: number
  reportFilterStore?: ReportFilterStore
}

interface State {
  groupBy: TimePeriod | null
  data?: ISourcesReportData[]
  isLoading: boolean
  overview: boolean
  locationGroupBy: 'all' | 'per location'
  sourceNames: string[]
}

@inject('reportFilterStore')
@observer
export class SourcesReport extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = {
      groupBy: null,
      data: undefined,
      isLoading: false,
      overview: true,
      locationGroupBy: 'all',
      sourceNames: [],
    }
  }

  componentDidMount = async () => {
    await this.getResults()
  }

  get query(): ISalesSourceQuery {
    const { selectedLocationIds, startDateString, endDateString } = this.props.reportFilterStore!
    const extraFields: SourceSalesReportFields[] =
      !this.state.overview || this.state.locationGroupBy !== 'all' ? ['Location'] : []

    return {
      locationIds: selectedLocationIds,
      startDate: startDateString,
      endDate: endDateString,
      orderBy: {
        direction: null,
        field: null,
      },
      groupBy: {
        time: this.state.groupBy,
        dimensions: [...extraFields, 'SourceName'],
      },
      fields: this.fields,
    }
  }

  get fields() {
    const extraFields: SourceSalesReportFields[] =
      !this.state.overview || this.state.locationGroupBy !== 'all' ? ['Location'] : []

    const date: SourceSalesReportFields[] = !this.state.overview ? ['Date'] : []

    const fields: SourceSalesReportFields[] = [...extraFields, ...date, 'SourceName', 'Total']
    return fields
  }

  parseSourceHeaders = async (data: ISourcesReportData[]) => {
    if (!data) {
      return []
    }
    const headers: string[] = []
    if (data) {
      data.forEach((row) => {
        Object.keys(row).forEach((sourceName) => {
          if (
            !headers.includes(sourceName) &&
            !['Total', 'Location', 'Date'].includes(sourceName)
          ) {
            headers.push(sourceName)
          }
        })
      })
    }

    this.setState({ sourceNames: headers })
  }

  addSourcesToData = async (data: ISourcesReportData[]) => {
    const dataWithAllHeaders: ISourcesReportData[] = []
    data.forEach((row) => {
      const sourceObject: ISourcesReportData = {
        ...row,
      }
      this.state.sourceNames.forEach((header) => {
        sourceObject[header] = row[header] || 0
      })
      dataWithAllHeaders.push(sourceObject)
    })
    return dataWithAllHeaders
  }

  getResults = async () => {
    const { companyId } = this.props
    const { startDate, endDate } = this.props.reportFilterStore!
    await this.setState({ data: [], isLoading: true })

    try {
      Track.startTimer('dashboard:reports_sources')
      const data = await API.getSalesBySource(companyId, this.query)
      Track.track('dashboard:reports_sources', {
        numberOfDays: moment(endDate).diff(startDate, 'days'),
      })
      await this.parseSourceHeaders(data)
      const dataWithAllSources = await this.addSourcesToData(data)

      this.setState({ data: dataWithAllSources, isLoading: false })
    } catch (error) {
      this.setState({ data: undefined, isLoading: false })
    }
  }

  tableRenderer = (rawData?: ISourcesReportData[]) => {
    if (!rawData) {
      return <div>Error</div>
    }

    if (rawData.length > 0) {
      return (
        <table className='table table-sm'>
          <thead>
            <tr>
              <th>Location</th>
              {!this.state.overview && <th>Date</th>}
              {this.state.sourceNames.map((sourceName: string, index: number) => {
                return (
                  <th key={`${index}-${sourceName}`} className='text-right'>
                    {sourceName}
                  </th>
                )
              })}
              <th className='text-right'>Total</th>
            </tr>
          </thead>
          <tbody>
            {rawData.map((data, index) => {
              return (
                <tr key={index}>
                  <td>{data.Location || 'All locations'}</td>
                  {!this.state.overview && <td>{data.Date && data.Date.substring(0, 10)}</td>}
                  {this.state.sourceNames.map((key) => {
                    return (
                      <td key={key} className='text-right'>
                        {GrPrice.convertDollarToDollar(data[key] as number)}
                      </td>
                    )
                  })}
                  <td className='text-right'>{GrPrice.convertDollarToDollar(data.Total)}</td>
                </tr>
              )
            })}
          </tbody>
        </table>
      )
    } else {
      return <div>No data</div>
    }
  }

  groupDataBy = (groupBy: TimePeriod) => {
    this.setState(
      {
        groupBy: groupBy,
      },
      this.getResults,
    )
  }

  exportCSV() {
    const { startDate, endDate } = this.props.reportFilterStore!
    const fields = this.fields.filter(
      (field: string) => field !== 'SourceName' && field !== 'Total',
    )
    const data = orderedExportData([...fields, ...this.state.sourceNames, 'Total'], this.state.data)
    if (data) {
      const title = this.state.overview ? 'overview' : 'over-time'
      parseAndDownloadCSV(`${title}-${startDate}-${endDate}`, data)
    }
  }

  render() {
    const { isLoading, overview } = this.state

    return (
      <>
        <Helmet>
          <title>Reports | Sources</title>
          <meta name='page-loaded' content='true' />
        </Helmet>
        <ReportFilter maxRangeMonths={12} />
        <div className='rounded background-white'>
          <div className='p-3'>
            <div>
              <b>Sources</b>
            </div>

            <div className='mt-2 mb-2'>
              <Radio.Group className='mr-2' defaultValue='overview' buttonStyle='solid'>
                <Radio.Button
                  onClick={async () =>
                    this.setState({ overview: true, groupBy: null }, await this.getResults)
                  }
                  className='btn-group'
                  value='overview'
                >
                  Overview
                </Radio.Button>
                <Radio.Button
                  onClick={async () =>
                    this.setState({ overview: false, groupBy: 'day' }, await this.getResults)
                  }
                  className='btn-group'
                  value='overtime'
                >
                  Over-time
                </Radio.Button>
              </Radio.Group>
              <UncontrolledButtonDropdown className='mr-2'>
                {overview ? (
                  <>
                    <DropdownToggle caret size='sm' color='light' style={{ textAlign: 'left' }}>
                      {this.state.locationGroupBy === 'all' ? 'Grouped locations' : 'Per location'}
                    </DropdownToggle>
                    <DropdownMenu>
                      <DropdownItem
                        onClick={async () =>
                          this.setState({ locationGroupBy: 'all' }, await this.getResults)
                        }
                      >
                        Grouped locations
                      </DropdownItem>
                      <DropdownItem
                        onClick={async () =>
                          this.setState({ locationGroupBy: 'per location' }, await this.getResults)
                        }
                      >
                        Per location
                      </DropdownItem>
                    </DropdownMenu>
                  </>
                ) : (
                  <>
                    <DropdownToggle caret size='sm' color='light' style={{ textAlign: 'left' }}>
                      Group by {this.state.groupBy}
                    </DropdownToggle>
                    <DropdownMenu>
                      <DropdownItem onClick={() => this.groupDataBy('day')}>day</DropdownItem>
                      <DropdownItem onClick={() => this.groupDataBy('week')}>week</DropdownItem>
                      <DropdownItem onClick={() => this.groupDataBy('month')}>month</DropdownItem>
                    </DropdownMenu>
                  </>
                )}
              </UncontrolledButtonDropdown>

              <button className='btn btn-light btn-sm' onClick={() => this.exportCSV()}>
                Export as csv
              </button>
            </div>

            <div className='table-responsive'>
              {isLoading ? <Skeleton active /> : this.tableRenderer(this.state.data)}
            </div>
          </div>
        </div>
      </>
    )
  }
}
